import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLibraries } from '../../../Pages/Library/ControlLibrary/store/ControlLibrary.action';
import { RightsAccess } from '../../helper';
import { useComponent } from '../../../Pages/UserSetting/UsersLibrary/UserRights';
import { LIBRARIES_MENU_ITEM_MAP, normalizeItemName } from '../../../constants/module-order-menu-mapping';
import MenuItem from '../MenuItem';
import { getModuleOrderIconComponent } from '../../../constants/module-order-icon-registry';
import {
  AssetLibraryIcon,
  CareLibraryIcon,
  ColourLibraryIcon,
  CostingLibraryIcon,
  CustomModulesIcon,
  DPPLibraryIcon,
  FreightDutyLibraryIcon,
  LibraryGroupIcon,
  MeasurementsIcon,
  PurchaseOrderLibraryIcon,
  SpecTemplateLibraryIcon,
  TestingLibraryIcon,
  ProductIdentityIcon,
} from '../../../bombiix-icon';

function LibraryTemplateURL({ isMobileMenu }) {
  const history = useHistory();
  const pathName = history?.location?.pathname;
  const dispatch = useDispatch();
  const reducer = useSelector((reducerstate) => reducerstate);
  const {
    Users: { logInRights, moduleAccess },
    SystemMastersDetails: { moduleOrderConfigs = [] },
  } = reducer;
  const controlLibraries = useSelector((s) => s.ControlLibraryReducer?.libraries || []);
  const [userRights, setUserRights] = useState({});
  const hasRequestedLibrariesRef = useRef(false);

  useEffect(() => {
    if (hasRequestedLibrariesRef.current || controlLibraries.length > 0) {
      return;
    }
    hasRequestedLibrariesRef.current = true;
    dispatch(fetchLibraries({ pageSize: 100 }));
  }, [dispatch, controlLibraries.length]);
  const { frieghtRight, customModuleRight, productCostingRight, purchaseOrderRight, TestingRight, DPPRight } = RightsAccess();
  const { createConditionalComponent } = useComponent();
  const BomRightsAccess = createConditionalComponent(userRights, 'libraries', 'bom');
  const ColourRightsAccess = createConditionalComponent(userRights, 'libraries', 'colours');
  const PomGradingRightsAccess = createConditionalComponent(userRights, 'libraries', 'pomGrading');
  const SpecRightsAccess = createConditionalComponent(userRights, 'libraries', 'spec');
  const CareRightsAccess = createConditionalComponent(userRights, 'libraries', 'care');
  const CareUserInstructionRightsAccess = createConditionalComponent(userRights, 'libraries', 'CareUserInstruction');
  const CustomRightsAccess = createConditionalComponent(userRights, 'libraries', 'customModule');
  const TestingRightsAccess = createConditionalComponent(userRights, 'libraries', 'testing');
  const PoTemplateRightsAccess = createConditionalComponent(userRights, 'libraries', 'poTemplate');
  const CostingTemplateRightsAccess = createConditionalComponent(userRights, 'libraries', 'costingTemplate');
  const rightsByKey = { frieghtRight, customModuleRight, productCostingRight, purchaseOrderRight, DPPRight, TestingRight };
  const permissionWrappers = {
    colours: ColourRightsAccess,
    bom: BomRightsAccess,
    pomGrading: PomGradingRightsAccess,
    spec: SpecRightsAccess,
    CareUserInstruction: CareUserInstructionRightsAccess,
    care: CareRightsAccess,
    customModule: CustomRightsAccess,
    dpp: null,
    testing: TestingRightsAccess,
    poTemplate: PoTemplateRightsAccess,
    costingTemplate: CostingTemplateRightsAccess,
    freight: null,
  };
  const RetailIdentifierRightsAccess = createConditionalComponent(userRights, 'libraries', 'retailIdentifier');

  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights);
    }
  }, [logInRights]);

  const librariesConfig = moduleOrderConfigs.find((c) => c.moduleId === 'librariesGroupsTemplates');
  const orderedItems = librariesConfig?.items
    ? [...librariesConfig.items].filter((i) => i.isActive !== false).sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    : null;

  const renderItem = (item, routeConfig) => {
    if (routeConfig.rightKey) {
      const rightVal = routeConfig.rightKey === 'DPPRight' ? DPPRight && moduleAccess?.dppAccess : rightsByKey[routeConfig.rightKey];
      if (!rightVal) return null;
    }
    const menuItem = (
      <MenuItem
        key={item.itemId || item.itemName}
        activeClassList={routeConfig.activeClassList || [routeConfig.path]}
        pathName={pathName}
        linkTo={routeConfig.path}
        IconComponent={getModuleOrderIconComponent(item.iconName ?? item.icon ?? routeConfig.menuIconClass ?? routeConfig.iconClass)}
        iconClass={routeConfig.menuIconClass || routeConfig.iconClass}
        titleKey={routeConfig.titleKey}
        isMobileMenu={isMobileMenu}
      />
    );
    const Wrapper = permissionWrappers[routeConfig.permissionKey];
    if (Wrapper) return <Wrapper key={item.itemId || item.itemName}>{menuItem}</Wrapper>;
    return <React.Fragment key={item.itemId || item.itemName}>{menuItem}</React.Fragment>;
  };

  const items = orderedItems
    ? orderedItems
        .map((item) => {
          const key = normalizeItemName(item.itemName);
          const routeConfig = LIBRARIES_MENU_ITEM_MAP[key];
          if (!routeConfig) return null;
          return renderItem(item, routeConfig);
        })
        .filter(Boolean)
    : null;

  const controlLibraryNavItems = (
    <>
      {controlLibraries.map((lib) => (
        <MenuItem
          key={lib._id}
          activeClassList={[`/libraries/control-library/${lib._id}`]}
          pathName={pathName}
          linkTo={`/libraries/control-library/${lib._id}`}
          iconUrl={lib.iconUrl || undefined}
          IconComponent={CustomModulesIcon}
          iconClass="icon-Module"
          titleKey={lib.libraryName}
          isMobileMenu={isMobileMenu}
        />
      ))}
    </>
  );

  if (items && items.length > 0) {
    return (
      <ul>
        {items}
        {controlLibraryNavItems}
      </ul>
    );
  }

  return (
    <ul>
      <ColourRightsAccess>
        <MenuItem
          activeClassList={['/libraries/colour-library', '/colour-groups']}
          pathName={pathName}
          linkTo="/libraries/colour-library"
          IconComponent={ColourLibraryIcon}
          titleKey="SUB_HEADER.COLOURS_ARTWORK"
          isMobileMenu={isMobileMenu}
        />
      </ColourRightsAccess>
      <BomRightsAccess>
        <MenuItem
          activeClassList={['/libraries/bom-type']}
          pathName={pathName}
          linkTo="/libraries/bom-type"
          IconComponent={LibraryGroupIcon}
          titleKey="SUB_HEADER.BOM"
          isMobileMenu={isMobileMenu}
        />
      </BomRightsAccess>
      <PomGradingRightsAccess>
        <MenuItem
          activeClassList={['/libraries/point-of-measures', '/pom-groups', '/gradings']}
          pathName={pathName}
          linkTo="/libraries/point-of-measures"
          IconComponent={MeasurementsIcon}
          titleKey="SUB_HEADER.POM_GRADINGS"
          isMobileMenu={isMobileMenu}
        />
      </PomGradingRightsAccess>
      <SpecRightsAccess>
        <MenuItem
          activeClassList={['/libraries/spec-template']}
          pathName={pathName}
          linkTo="/libraries/spec-template"
          IconComponent={SpecTemplateLibraryIcon}
          titleKey="SUB_HEADER.SPEC_TEMPLATES"
          isMobileMenu={isMobileMenu}
        />
      </SpecRightsAccess>
      <CareRightsAccess>
        <MenuItem
          activeClassList={['/libraries/care-user-instruction', '/care-group']}
          pathName={pathName}
          linkTo="/libraries/care-user-instruction"
          IconComponent={CareLibraryIcon}
          titleKey="SUB_HEADER.CARE_USER_LABELS"
          isMobileMenu={isMobileMenu}
        />
      </CareRightsAccess>
      <CustomRightsAccess>
        {customModuleRight && (
          <MenuItem
            activeClassList={['/libraries/custom-module/library', 'custom-module/group']}
            pathName={pathName}
            linkTo="/libraries/custom-module/library"
            IconComponent={CustomModulesIcon}
            titleKey="SUB_HEADER.ADDITIONAL_INFORMATION"
            isMobileMenu={isMobileMenu}
          />
        )}
      </CustomRightsAccess>
      {controlLibraryNavItems}
      {DPPRight && moduleAccess?.dppAccess && (
        <MenuItem
          activeClassList={['/libraries/dpp']}
          pathName={pathName}
          linkTo="/libraries/dpp"
          IconComponent={DPPLibraryIcon}
          titleKey="SUB_HEADER.DPP"
          isMobileMenu={isMobileMenu}
        />
      )}
      <RetailIdentifierRightsAccess>
        <MenuItem
          activeClassList={['/libraries/retail-identifier']}
          pathName={pathName}
          linkTo="/libraries/retail-identifier"
          IconComponent={ProductIdentityIcon}
          titleKey="SUB_HEADER.RETAIL_IDENTIFIER"
          isMobileMenu={isMobileMenu}
        />
      </RetailIdentifierRightsAccess>
      <TestingRightsAccess>
        {TestingRight && (
          <MenuItem
            activeClassList={['/libraries/testing']}
            pathName={pathName}
            linkTo="/libraries/testing"
            IconComponent={TestingLibraryIcon}
            titleKey="SUB_HEADER.TESTING"
            isMobileMenu={isMobileMenu}
          />
        )}
      </TestingRightsAccess>
      {purchaseOrderRight && (
        <PoTemplateRightsAccess>
          <MenuItem
            activeClassList={['/libraries/purchase-order-template']}
            pathName={pathName}
            linkTo="/libraries/purchase-order-template"
            IconComponent={PurchaseOrderLibraryIcon}
            titleKey="SUB_HEADER.PO_TEMPLATES"
            isMobileMenu={isMobileMenu}
          />
        </PoTemplateRightsAccess>
      )}
      {productCostingRight && (
        <CostingTemplateRightsAccess>
          <MenuItem
            activeClassList={['/libraries/costing-template']}
            pathName={pathName}
            linkTo="/libraries/costing-template"
            IconComponent={CostingLibraryIcon}
            titleKey="SUB_HEADER.COSTING_TEMPLATE"
            isMobileMenu={isMobileMenu}
          />
        </CostingTemplateRightsAccess>
      )}
      {frieghtRight && (
        <MenuItem
          activeClassList={['/libraries/freight-duty', '/libraries/freight', '/libraries/duty']}
          pathName={pathName}
          linkTo="/libraries/freight-duty?tab=freight"
          IconComponent={FreightDutyLibraryIcon}
          titleKey="SUB_HEADER.FREIGHT_AND_DUTY"
          isMobileMenu={isMobileMenu}
        />
      )}
      <MenuItem
        activeClassList={['/libraries/asset-library']}
        pathName={pathName}
        linkTo="/libraries/asset-library"
        IconComponent={AssetLibraryIcon}
        iconClass="icon-DAM-1"
        titleKey="ASSET_LIBRARY.SYSTEM_MASTER_TAB"
        isMobileMenu={isMobileMenu}
      />
    </ul>
  );
}

export default LibraryTemplateURL;
